import type { LucideIcon } from 'lucide-react';
import IconButton from '@savvy-kit/components/icon-button/icon-button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@savvy-kit/components/tooltip/tooltip';
import { cn } from '@savvy-kit/lib/utils';
import { DownloadIcon, Trash2Icon } from 'lucide-react';

import { formatFileSize } from '../file-drop-zone';
import { getFileIcon } from './utils/get-file-icon';

export type AttachmentItemProps = {
  name: string;
  size?: number;
  src?: string;
  onOpen?: () => void;
  onDownload?: () => void;
  onRemove?: () => void;
  className?: string;
};

function AttachmentAction({
  icon: Icon,
  label,
  onClick,
  destructive,
}: {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
  destructive?: boolean;
}) {
  return (
    <IconButton
      variant="ghost"
      size="sm"
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
      className={cn('shrink-0', destructive && 'text-destructive')}
    >
      <Icon className="size-4" aria-hidden="true" />
    </IconButton>
  );
}

export function AttachmentItem({ name, size, src, onOpen, onDownload, onRemove, className }: AttachmentItemProps) {
  const Icon = getFileIcon(name);

  const nameEl = onOpen ? (
    <button type="button" onClick={onOpen} className="max-w-[14rem] truncate text-dark">
      {name}
    </button>
  ) : (
    <span className="max-w-[14rem] truncate text-dark">{name}</span>
  );

  return (
    <div
      data-slot="attachment-item"
      className={cn(
        'inline-flex items-center gap-2 rounded-md border border-light bg-card py-1 pl-1.5 pr-1.5 text-sm',
        className,
      )}
    >
      {src ? (
        <img src={src} alt="" className="size-6 shrink-0 rounded object-cover" />
      ) : (
        <span className="flex size-6 shrink-0 items-center justify-center rounded bg-slate-100">
          <Icon className="size-3.5 text-secondary" aria-hidden="true" />
        </span>
      )}

      <Tooltip>
        <TooltipTrigger render={nameEl} />
        <TooltipContent>{name}</TooltipContent>
      </Tooltip>

      {size != null && <span className="shrink-0 text-xs tabular-nums text-secondary">{formatFileSize(size)}</span>}

      {onDownload && <AttachmentAction icon={DownloadIcon} label={`Download ${name}`} onClick={onDownload} />}
      {onRemove && <AttachmentAction icon={Trash2Icon} label={`Remove ${name}`} onClick={onRemove} destructive />}
    </div>
  );
}
